import { NavLink, useMatch } from 'react-router-dom';
import {
  Nav,
  NavBody,
  NavFooter,
  NavItem,
  NavList,
  Sidebar,
  SidebarToggle,
} from '@optiaxiom/react';
import { IconAngleRight, IconArrowsRepeat, IconBell } from '@optimizely/axiom-icons';
import { useFeature } from '@optimizely/react-sdk';

import { NavigationItem } from '@frontend/components/ui/NavigationItem';
import { useLocalStorage, useOperations, useUnreadThread, useUxFeatures } from '@frontend/hooks';
import { InstanceOperationsName, OperationsName } from '@frontend/types';
import { isDesktopApp } from '@frontend/utils/desktop';

import {
  IconAgentRegular,
  IconBookRegular,
  IconCommentsRegular,
  IconDatabaseRegular,
  IconFolderBookmarkRegular,
  IconGearRegular,
  IconScrewdriverWrenchRegular,
} from '../icons';

type Route = {
  path: string;
  icon: JSX.Element;
  label: string;
  permissions: OperationsName[];
  instanceCapabilities?: InstanceOperationsName[];
  featureFlag?: string;
  footerNav?: boolean;
};

export const LeftNavbar = () => {
  const { canUser, canInstance } = useOperations();
  const [sidebarExpanded, setSidebarExpanded] = useLocalStorage('sidebar-expanded', false);
  const { hasUnreadThreads } = useUnreadThread();
  // Determine if the current route is the chat route
  const isOnChatRoute = useMatch('/instances/:instanceId/chat');

  const [isToolsAuthConnectionEnabled] = useFeature('tools_auth_connection');
  const { reorgRagContext } = useUxFeatures();

  const routes: Route[] = [
    {
      path: 'chat',
      icon: <IconCommentsRegular />,
      label: 'Chats',
      permissions: ['use_chat'],
    },
    {
      path: 'context',
      icon: <IconFolderBookmarkRegular />,
      label: 'Context',
      // We should add view_context or similar permission
      permissions: ['view_instructions', 'manage_instructions', 'use_chat', 'manage_rag_config'],
    },
    {
      path: 'instructions',
      icon: <IconBookRegular />,
      label: 'Instructions',
      permissions: ['view_instructions', 'manage_instructions', 'use_chat'],
    },
    {
      path: 'notifications',
      icon: <IconBell />,
      label: 'Notifications',
      permissions: ['use_chat'], // TODO: support empty permissions array for truly public pages
      featureFlag: 'notifications',
    },
    {
      path: 'agents',
      icon: <IconAgentRegular />,
      label: 'Agents',
      permissions: ['view_agents'],
    },
    {
      path: 'tools',
      icon: <IconScrewdriverWrenchRegular />,
      label: 'Tools',
      permissions: ['view_tools', 'use_chat'],
    },
    {
      path: 'rag',
      icon: <IconDatabaseRegular />,
      label: 'RAG',
      permissions: ['manage_rag_config'],
      instanceCapabilities: ['RAG_enabled'],
      featureFlag: 'shamwow-settings-page',
    },
    {
      path: 'settings',
      icon: <IconGearRegular />,
      label: 'Settings',
      permissions: ['use_chat'],
    },
  ] as const;

  const renderNavItem = (route: Route) => (
    <NavigationItem
      key={route.path}
      path={route.path}
      icon={route.icon}
      label={route.label}
      permissions={route.permissions}
      instanceCapabilities={route.instanceCapabilities}
      featureFlag={route.featureFlag}
      showIndicator={route.path === 'chat' && hasUnreadThreads && !isOnChatRoute}
    />
  );

  const renderNavItems = ({ isFooterNav }: { isFooterNav: boolean }) =>
    routes
      .filter(route => {
        // Check permission
        if (!canUser(route.permissions)) return false;

        // Check instance capabilities
        if (route.instanceCapabilities && !canInstance(route.instanceCapabilities)) return false;

        // Check footer nav
        if (Boolean(route.footerNav) !== isFooterNav) return false;

        // Hide RAG & Instructions tabs when reorg_rag_context is enabled
        if ((route.path === 'rag' || route.path === 'instructions') && reorgRagContext)
          return false;

        // Hide Context tab when reorg_rag_context is disabled
        if (route.path === 'context' && !reorgRagContext) return false;

        // Special handling for Tools: only apply FF for use_chat users
        if (route.path === 'tools') {
          // If user has view_tools, always show (ignore FF)
          if (canUser(['view_tools'])) return true;
          // Otherwise, check FF
          return isToolsAuthConnectionEnabled;
        }

        return true;
      })
      .map(renderNavItem);

  return (
    <Sidebar defaultExpanded={sidebarExpanded} onExpandedChange={setSidebarExpanded}>
      <Nav>
        <NavBody>
          <NavList>{renderNavItems({ isFooterNav: false })}</NavList>
        </NavBody>

        <NavFooter>
          <NavList>
            {isDesktopApp() && (
              <NavLink to="/instances" style={{ textDecoration: 'none', color: 'inherit' }}>
                <NavItem icon={<IconArrowsRepeat />}>Switch Instance</NavItem>
              </NavLink>
            )}
            <SidebarToggle icon={<IconAngleRight />} />
            {renderNavItems({ isFooterNav: true })}
          </NavList>
        </NavFooter>
      </Nav>
    </Sidebar>
  );
};
